import * as React from 'react';
import { Loader2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn, ErrorBanner } from '@klareospace/pharos-lib-web-utils';
import { IMPORT_SUPPORTED_EXTENSIONS, MAX_IMPORT_FILE_SIZE_BYTES } from '@klareospace/pharos-lib-types';

import { MAX_IMPORT_FILE_SIZE_MB } from '../config';

// ─── Constants ──────────────────────────────────────────────────────────────

// The accept attribute and filename checks need the dotted form — map the shared bare
// extensions to dotted once rather than re-listing them by hand.
const DOTTED_EXTENSIONS = IMPORT_SUPPORTED_EXTENSIONS.map((extension) => `.${extension}`);
const ACCEPT_ATTRIBUTE = DOTTED_EXTENSIONS.join(',');

// ─── Props ────────────────────────────────────────────────────────────────────

export interface FileDropzoneProps {
  /** Disables interaction and shows a spinner while an upload is in progress. */
  isUploading: boolean;
  /** Called with the selected file as soon as the user drops or picks a valid one — the upload starts immediately. */
  onFileSelected: (file: File) => void;
  /** Called whenever the local validation error is set or cleared, so a parent-rendered error (e.g. a mutation `ErrorBanner`) can stay mutually exclusive with it. */
  onValidationErrorChange?: (hasValidationError: boolean) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Drag-and-drop and click-to-browse file picker for `.csv`/`.xlsx` order import files. */
export function FileDropzone({
  isUploading,
  onFileSelected,
  onValidationErrorChange,
}: FileDropzoneProps): React.JSX.Element {
  const { t } = useTranslation('imports');
  const [isDragging, setIsDragging] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  function setValidationErrorState(message: string | null): void {
    setValidationError(message);
    onValidationErrorChange?.(message !== null);
  }

  function handleFile(file: File): void {
    if (!hasAcceptedExtension(file.name)) {
      setValidationErrorState(t('dropzone.invalidExtension'));
      return;
    }
    if (!isWithinSizeLimit(file)) {
      setValidationErrorState(t('dropzone.fileTooLarge', { maxSizeMb: MAX_IMPORT_FILE_SIZE_MB }));
      return;
    }
    setValidationErrorState(null);
    onFileSelected(file);
  }

  function handleClick(): void {
    if (isUploading) return;
    inputRef.current?.click();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (!isActivationKey(event.key)) return;
    event.preventDefault();
    handleClick();
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
    event.target.value = '';
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    if (isUploading) return;
    setIsDragging(true);
  }

  function handleDragLeave(event: React.DragEvent<HTMLDivElement>): void {
    // Moving over a child (icon/text) fires a leave immediately followed by an enter on the
    // child — ignore it when the pointer is still within the dropzone to avoid flicker.
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsDragging(false);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragging(false);
    if (isUploading) return;
    const file = event.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  return (
    <div>
      <div
        aria-disabled={isUploading}
        className={cn(
          'flex min-h-52 flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-border',
          isUploading ? 'pointer-events-none opacity-70' : 'cursor-pointer hover:border-primary/50',
        )}
        data-testid="file-dropzone"
        onClick={handleClick}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <input
          accept={ACCEPT_ATTRIBUTE}
          aria-label={t('dropzone.inputAriaLabel')}
          className="hidden"
          disabled={isUploading}
          onChange={handleInputChange}
          ref={inputRef}
          type="file"
        />
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10">
          {isUploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          ) : (
            <Upload className="h-5 w-5 text-primary" />
          )}
        </div>
        <p className="text-sm font-medium text-foreground">
          {isUploading ? t('dropzone.uploading') : t('dropzone.prompt')}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('dropzone.helperText', { maxSizeMb: MAX_IMPORT_FILE_SIZE_MB })}
        </p>
      </div>
      {validationError && <ErrorBanner className="mt-2" message={validationError} />}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isActivationKey(key: string): boolean {
  return key === 'Enter' || key === ' ';
}

function hasAcceptedExtension(filename: string): boolean {
  const lowerFilename = filename.toLowerCase();
  return DOTTED_EXTENSIONS.some((extension) => lowerFilename.endsWith(extension));
}

function isWithinSizeLimit(file: File): boolean {
  return file.size <= MAX_IMPORT_FILE_SIZE_BYTES;
}
